import React, { useEffect, useState } from "react";
import { BookOpen, Clock, CheckCircle2, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { Container, SectionHeader } from "@/components/Section";
import { useEnquiry } from "@/context/EnquiryContext";
import { api } from "@/lib/api";

export default function Courses() {
  const { openEnquiry } = useEnquiry();
  const [courses, setCourses] = useState([]);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="courses-page">
      <Seo title="Courses" description="Prasad Tutor Group (PTG) offers coaching from Nursery to Standard 10, Commerce for Standard 11 & 12, and degree-level accounting subjects." />
      <section className="bg-blue-950 py-16 sm:py-20 text-white">
        <Container>
          <SectionHeader center={false} light eyebrow="Our Courses" title="Programmes designed for real progress"
            subtitle="Each course focuses on strong fundamentals, consistent practice and board-level readiness." />
        </Container>
      </section>

      <section className="py-16 sm:py-24">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08}>
                <div className="h-full flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all overflow-hidden" data-testid={`course-card-${i}`}>
                  <div className="bg-gradient-to-br from-blue-900 to-blue-800 p-6 text-white">
                    <BookOpen className="h-8 w-8 text-sky-300" />
                    <h3 className="mt-3 text-2xl font-bold">{c.title}</h3>
                    <p className="text-sky-200 text-sm">{c.subtitle}</p>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-slate-600 text-sm leading-relaxed">{c.description}</p>

                    <div className="mt-5">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">What We Cover</h4>
                      <div className="mt-3 space-y-2.5">
                        {(c.levels && c.levels.length ? c.levels : c.subjects || []).map((lv, idx) => {
                          const ci = lv.indexOf(":");
                          const label = ci > -1 ? lv.slice(0, ci).trim() : lv.trim();
                          const detail = ci > -1 ? lv.slice(ci + 1).trim() : "";
                          return (
                            <div key={idx} className="flex items-start gap-2.5" data-testid={`course-level-${i}-${idx}`}>
                              <CheckCircle2 className="h-4 w-4 mt-0.5 text-sky-600 shrink-0" />
                              <p className="text-sm text-slate-700 leading-snug">
                                <span className="font-semibold text-slate-900">{label}</span>
                                {detail ? <span className="text-slate-600">: {detail}</span> : null}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {c.methodology && (
                      <div className="mt-5">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Teaching Methodology</h4>
                        <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.methodology}</p>
                      </div>
                    )}

                    {c.batch_info && (
                      <div className="mt-5 flex items-start gap-2 text-sm text-slate-600">
                        <Clock className="h-4 w-4 mt-0.5 text-blue-900 shrink-0" /> {c.batch_info}
                      </div>
                    )}

                    {c.fees && (
                      <div className="mt-3 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                        <IndianRupee className="h-4 w-4 text-blue-900" /> {c.fees}
                      </div>
                    )}

                    <Button onClick={() => openEnquiry(c.title)} className="mt-6 w-full bg-amber-500 hover:bg-amber-600 font-bold" data-testid={`course-enquire-${i}`}>
                      Admission Enquiry
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          {courses.length === 0 && <p className="text-center text-slate-500">Courses will appear here soon.</p>}
        </Container>
      </section>
    </div>
  );
}
